import { useEffect, useState, useMemo, useRef } from 'react'
import { Search, CheckCircle2, FileText, BookOpen, Link as LinkIcon, Moon, Sun, Maximize, Minimize, Plus, Target, Command, X } from 'lucide-react'
import { useTaskStore } from '../../store/taskStore'
import { toggleFullscreen, isFullscreen } from '../../services/fullscreen'

interface CommandItem {
  id: string
  category: 'Ações' | 'Tarefas' | 'Notas' | 'Diário' | 'Links'
  title: string
  subtitle?: string
  icon: React.ReactNode
  action: () => void
}

export default function CommandPalette() {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [selectedIndex, setSelectedIndex] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)

  const tasks = useTaskStore((s) => s.tasks)
  const notes = useTaskStore((s) => s.notes)
  const diaryEntries = useTaskStore((s) => s.diaryEntries)
  const links = useTaskStore((s) => s.links)
  const accessibility = useTaskStore((s) => s.accessibility)
  const updateAccessibility = useTaskStore((s) => s.updateAccessibility)
  const selectTask = useTaskStore((s) => s.selectTask)

  const isDark = Boolean(accessibility.darkMode)

  // Toggle with Ctrl+K / Cmd+K
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setOpen((prev) => !prev)
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  // Focus input when opened
  useEffect(() => {
    if (open) {
      setQuery('')
      setSelectedIndex(0)
      setTimeout(() => inputRef.current?.focus(), 50)
    }
  }, [open])

  // Build items list
  const items = useMemo<CommandItem[]>(() => {
    const q = query.trim().toLowerCase()
    const list: CommandItem[] = []

    // 1. Actions
    const actions: CommandItem[] = [
      {
        id: 'action-toggle-theme',
        category: 'Ações',
        title: isDark ? 'Ativar Modo Claro' : 'Ativar Modo Escuro',
        subtitle: 'Alterna o tema visual da aplicação',
        icon: isDark ? <Sun size={16} className="text-yellow-400" /> : <Moon size={16} className="text-indigo-400" />,
        action: () => {
          updateAccessibility({ darkMode: !isDark })
          setOpen(false)
        },
      },
      {
        id: 'action-toggle-fullscreen',
        category: 'Ações',
        title: isFullscreen() ? 'Sair da Tela Cheia' : 'Entrar em Tela Cheia',
        subtitle: 'Expande ou restaura o tamanho da tela',
        icon: isFullscreen() ? <Minimize size={16} /> : <Maximize size={16} />,
        action: () => {
          void toggleFullscreen()
          setOpen(false)
        },
      },
    ]

    actions.forEach((act) => {
      if (!q || act.title.toLowerCase().includes(q) || (act.subtitle && act.subtitle.toLowerCase().includes(q))) {
        list.push(act)
      }
    })

    // 2. Tasks
    tasks.forEach((t) => {
      if (!q || t.title.toLowerCase().includes(q) || t.description?.toLowerCase().includes(q) || t.tags.some((tag) => tag.toLowerCase().includes(q))) {
        list.push({
          id: `task-${t.id}`,
          category: 'Tarefas',
          title: t.title,
          subtitle: `Status: ${t.status === 'done' ? 'Concluída' : t.status === 'in-progress' ? 'Em andamento' : 'A fazer'} | Prioridade: ${t.priority}`,
          icon: <CheckCircle2 size={16} className={t.status === 'done' ? 'text-green-500' : 'text-blue-500'} />,
          action: () => {
            selectTask(t.id)
            setOpen(false)
          },
        })
      }
    })

    // 3. Notes
    notes.forEach((n) => {
      if (!q || n.title.toLowerCase().includes(q) || n.content.toLowerCase().includes(q)) {
        list.push({
          id: `note-${n.id}`,
          category: 'Notas',
          title: n.title || 'Nota sem título',
          subtitle: n.content.substring(0, 60) + (n.content.length > 60 ? '...' : ''),
          icon: <FileText size={16} className="text-amber-500" />,
          action: () => {
            setOpen(false)
          },
        })
      }
    })

    // 4. Diary Entries
    diaryEntries.forEach((d) => {
      if (!q || d.title.toLowerCase().includes(q) || d.content.toLowerCase().includes(q) || d.date.includes(q)) {
        list.push({
          id: `diary-${d.id}`,
          category: 'Diário',
          title: d.title || `Diário - ${d.date}`,
          subtitle: d.content.substring(0, 60) + (d.content.length > 60 ? '...' : ''),
          icon: <BookOpen size={16} className="text-purple-500" />,
          action: () => {
            setOpen(false)
          },
        })
      }
    })

    // 5. Global Links
    links.forEach((l) => {
      if (!q || l.title.toLowerCase().includes(q) || l.url.toLowerCase().includes(q)) {
        list.push({
          id: `link-${l.id}`,
          category: 'Links',
          title: l.title,
          subtitle: l.url,
          icon: <LinkIcon size={16} className="text-teal-500" />,
          action: () => {
            window.open(l.url, '_blank')
            setOpen(false)
          },
        })
      }
    })

    return list
  }, [query, isDark, tasks, notes, diaryEntries, links, updateAccessibility, selectTask])

  // Reset selected index on query change
  useEffect(() => {
    setSelectedIndex(0)
  }, [query])

  // Handle key navigation inside input
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setSelectedIndex((prev) => (items.length > 0 ? (prev + 1) % items.length : 0))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setSelectedIndex((prev) => (items.length > 0 ? (prev - 1 + items.length) % items.length : 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (items[selectedIndex]) {
        items[selectedIndex].action()
      }
    } else if (e.key === 'Escape') {
      e.preventDefault()
      setOpen(false)
    }
  }

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-[30000] flex items-start justify-center bg-slate-950/60 p-4 pt-16 sm:pt-24 backdrop-blur-sm"
      onMouseDown={(e) => e.target === e.currentTarget && setOpen(false)}
    >
      <div className="flex w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-slate-900/10 dark:bg-slate-900 dark:ring-slate-800 dark:text-slate-100 animate-in fade-in zoom-in-95 duration-150">
        {/* Search Bar */}
        <div className="flex items-center border-b border-slate-200 px-4 py-3 dark:border-slate-800">
          <Search size={18} className="mr-3 text-slate-400 shrink-0" />
          <input
            ref={inputRef}
            type="text"
            className="w-full bg-transparent text-base text-slate-900 placeholder-slate-400 focus:outline-none dark:text-slate-100 dark:placeholder-slate-500"
            placeholder="Digite para buscar tarefas, notas, links ou comandos..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <kbd className="hidden sm:inline-flex items-center gap-1 rounded bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-500 dark:bg-slate-800 dark:text-slate-400">
            Esc
          </kbd>
          <button
            type="button"
            className="ml-2 icon-button p-1"
            onClick={() => setOpen(false)}
            aria-label="Fechar"
          >
            <X size={18} />
          </button>
        </div>

        {/* Results List */}
        <div className="max-h-[60vh] overflow-y-auto p-2">
          {items.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-500 dark:text-slate-400">
              Nenhum resultado encontrado para &quot;<span className="font-semibold text-slate-700 dark:text-slate-300">{query}</span>&quot;.
            </div>
          ) : (
            <ul className="space-y-1">
              {items.map((item, index) => {
                const isSelected = index === selectedIndex
                return (
                  <li key={item.id}>
                    <button
                      type="button"
                      className={`flex w-full items-center justify-between rounded-xl px-3 py-2.5 text-left text-sm transition-colors ${
                        isSelected
                          ? 'bg-blue-600 text-white dark:bg-blue-600'
                          : 'text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800/80'
                      }`}
                      onClick={() => item.action()}
                      onMouseEnter={() => setSelectedIndex(index)}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div
                          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${
                            isSelected
                              ? 'bg-blue-500/30 text-white'
                              : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300'
                          }`}
                        >
                          {item.icon}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="truncate font-medium">{item.title}</div>
                          {item.subtitle && (
                            <div
                              className={`truncate text-xs ${
                                isSelected ? 'text-blue-100' : 'text-slate-400 dark:text-slate-400'
                              }`}
                            >
                              {item.subtitle}
                            </div>
                          )}
                        </div>
                      </div>
                      <span
                        className={`ml-3 shrink-0 rounded px-2 py-0.5 text-[10px] font-semibold tracking-wide uppercase ${
                          isSelected
                            ? 'bg-blue-500/40 text-white'
                            : 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400'
                        }`}
                      >
                        {item.category}
                      </span>
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
        </div>

        {/* Footer info */}
        <div className="flex items-center justify-between border-t border-slate-200 bg-slate-50 px-4 py-2 text-xs text-slate-500 dark:border-slate-800 dark:bg-slate-900/50 dark:text-slate-400">
          <div className="flex items-center gap-3">
            <span>
              <kbd className="rounded bg-slate-200 px-1 py-0.5 font-mono text-[10px] dark:bg-slate-800">↑</kbd>
              <kbd className="ml-0.5 rounded bg-slate-200 px-1 py-0.5 font-mono text-[10px] dark:bg-slate-800">↓</kbd> Navegar
            </span>
            <span>
              <kbd className="rounded bg-slate-200 px-1 py-0.5 font-mono text-[10px] dark:bg-slate-800">↵</kbd> Selecionar
            </span>
          </div>
          <span className="flex items-center gap-1 font-medium">
            <Command size={12} /> TaskPriority Palette
          </span>
        </div>
      </div>
    </div>
  )
}

